import React, { useState, useMemo } from 'react';
import ConcessionChart from './charts/ConcessionChart';
import ConcessionCountChart from './charts/ConcessionCountChart';
import ConcessionTransactionChart from './charts/ConcessionTransactionChart';
import AnalysisWindow from './components/AnalysisWindow';
import moment from 'moment';
import { Panel, PanelHeader, SortMenu } from './components/ConcessionUI';
import AdaptiveBoardLayout from './components/AdaptiveBoardLayout';
import Icon from '../../components/atoms/Icon';
import { ChartSegmented } from './charts/chartTheme';
import { getQueueTimeStatus, getPeopleCountStatus, STATUS_COLORS, QUEUE_TIME_GOOD_MAX, formatQueueValue } from './concessionTheme';
import {
  seriesBySection,
  averageSeries,
  minutesOver,
  overflowSummary,
  overflowAcrossSections,
  peakOf,
  deltaSeries,
  gameTimes,
  formatClock,
  formatDuration,
} from './live/liveMetrics';
import DaySummary from './live/DaySummary';
import { buildConcessionDayInsights } from './live/dayInsights';
import OverviewInsights from './components/OverviewInsights';
import { Sparkline, unitFormatter, KpiCard, SectionTile, OverflowAlert } from './live/LiveComponents';
import './concession.css';
import { Slot, hasSlot } from './extensions/Slot';

// People count thresholds (same as the live view and location card)
const COUNT_SECTION_LOW = 15;
const COUNT_SECTION_HIGH = 30;
const COUNT_AVG_LOW = 20;
const COUNT_AVG_HIGH = 40;

const STATUS_LABELS = {
  queue: { good: 'On target', bad: 'Over target' },
  count: { good: 'Light', warn: 'Moderate', bad: 'Busy' },
};

const ConcessionLocationHistoricalView = ({
  // State
  historicalData,
  historicalTransactionData,
  historicalPeopleCountData,
  stats,
  countStats,
  currentGame,

  // Section Analysis sorting state
  isSectionAnalysisDropdownOpen,
  currentSectionAnalysisSort,
  
  // Computed data
  sections,
  
  // Section Analysis sorting data
  sectionAnalysisSortOptions,
  sortedSectionAnalysisSections,
  
  // Current location data
  currentLocation,
  authCtx,
  selectedDate,
  extension,
  
  // Section Analysis sort actions
  setIsSectionAnalysisDropdownOpen,
  setCurrentSectionAnalysisSort,
  handleSectionAnalysisSortChange,
  toggleSectionAnalysisDropdown,
  
  // Utility functions
  formatTransactionTime,

  // Add these from the controller
  calculateStats,
  calculateCountStats
}) => {
  // Determine if we're in count mode or queue mode
  const isCountMode = currentLocation?.sensorDataType === "count";
  
  // Check if we should show delta stats (only when 2+ sections AND in queue mode)
  const showDeltaStats = sections.length >= 2 && !isCountMode;

  // Time range state - using applied times for filtering
  const [appliedStartTime, setAppliedStartTime] = useState(null);
  const [appliedEndTime, setAppliedEndTime] = useState(null);
  const [sectionView, setSectionView] = useState('cards');
  const [trendTab, setTrendTab] = useState('queue');
  // Section trends: collapsed by default on live game days, open on past games
  const [trendsOpen, setTrendsOpen] = useState(true);

  // Find the earliest and latest time points from all available data
  const getDataTimeRange = () => {
    let allTimePoints = [];
    if (isCountMode && historicalPeopleCountData.length > 0) {
      allTimePoints = historicalPeopleCountData.flatMap(section => 
        section.data.map(point => new Date(point.x))
      );
    } else if (!isCountMode && historicalData.length > 0) {
      allTimePoints = historicalData.flatMap(section => 
        section.data.map(point => new Date(point.x))
      );
    }
    
    if (allTimePoints.length === 0) return { earliest: null, latest: null };
    
    const earliest = new Date(Math.min(...allTimePoints));
    const latest = new Date(Math.max(...allTimePoints));
    return { earliest, latest };
  };

  const { earliest: earliestTime, latest: latestTime } = getDataTimeRange();

  // Handle time range changes from the slider
  const handleTimeRangeChange = (startTime, endTime) => {
    setAppliedStartTime(startTime);
    setAppliedEndTime(endTime);
  };

  // Filter data based on time range (both start and end)
  const getFilteredData = (data, startTime, endTime) => {
    if (!startTime || !endTime || !data?.length) return data;
    
    return data.map(sectionData => ({
      ...sectionData,
      data: sectionData.data.filter(point => {
        const pointTime = new Date(point.x);
        return pointTime >= startTime && pointTime <= endTime;
      })
    })).filter(sectionData => sectionData.data.length > 0);
  };

  // Get current section times based on filtered data
  const getFilteredCurrentSectionTimes = (filteredData, isTransactionData = false) => {
    const currentTimes = {};
    
    filteredData.forEach(sectionData => {
      const latestPoint = sectionData.data[sectionData.data.length - 1];
      if (latestPoint) {
        currentTimes[sectionData.section] = latestPoint.y || 0;
      }
    });
    
    return currentTimes;
  };

  // Memoized filtered data and calculated stats - using APPLIED time values
  const filteredDataAndStats = useMemo(() => {
    // If we don't have valid time range yet, return original data
    if (!appliedStartTime || !appliedEndTime) {
      return {
        filteredHistoricalData: historicalData || [],
        filteredHistoricalTransactionData: historicalTransactionData || [],
        filteredHistoricalPeopleCountData: historicalPeopleCountData || [],
        filteredStats: stats,
        filteredCountStats: countStats
      };
    }
    
    if (isCountMode) {
      const filteredCountData = getFilteredData(historicalPeopleCountData, appliedStartTime, appliedEndTime);
      const filteredCurrentCounts = getFilteredCurrentSectionTimes(filteredCountData);
      const filteredCountStats = calculateCountStats ? 
        calculateCountStats(filteredCountData, filteredCurrentCounts) : countStats;
      
      return {
        filteredHistoricalData: [],
        filteredHistoricalTransactionData: [],
        filteredHistoricalPeopleCountData: filteredCountData,
        filteredStats: null,
        filteredCountStats: filteredCountStats
      };
    } else {
      const filteredQueueData = getFilteredData(historicalData, appliedStartTime, appliedEndTime);
      const filteredTransactionData = getFilteredData(historicalTransactionData, appliedStartTime, appliedEndTime);
      const filteredCurrentQueueTimes = getFilteredCurrentSectionTimes(filteredQueueData);
      const filteredCurrentTransactionTimes = getFilteredCurrentSectionTimes(filteredTransactionData, true);
      const filteredStats = calculateStats ? 
        calculateStats(filteredQueueData, filteredCurrentQueueTimes, filteredTransactionData, filteredCurrentTransactionTimes) : stats;
      
      return {
        filteredHistoricalData: filteredQueueData,
        filteredHistoricalTransactionData: filteredTransactionData,
        filteredHistoricalPeopleCountData: [],
        filteredStats: filteredStats,
        filteredCountStats: null
      };
    }
  }, [
    historicalData, 
    historicalTransactionData, 
    historicalPeopleCountData, 
    appliedStartTime, 
    appliedEndTime, 
    isCountMode,
    calculateStats,
    calculateCountStats,
    stats,
    countStats
  ]);

  const {
    filteredHistoricalData,
    filteredHistoricalTransactionData,
    filteredHistoricalPeopleCountData,
    filteredStats,
    filteredCountStats
  } = filteredDataAndStats;

  // Use filtered stats for display
  const displayStats = filteredStats || stats;
  const displayCountStats = filteredCountStats || countStats;

  // Show loading only if we have data but times aren't ready
  if ((historicalData.length > 0 || historicalPeopleCountData.length > 0) && 
      (!earliestTime || !latestTime)) {
    return <div>Loading...</div>;
  }

  const unit = (text) => <span className="cx-unit">{text}</span>;

  // ---- Derived series over the selected range ----
  const mainSeries = seriesBySection(isCountMode ? filteredHistoricalPeopleCountData : filteredHistoricalData);
  const txnSeries = seriesBySection(filteredHistoricalTransactionData);
  const avgSeries = averageSeries(mainSeries);
  const avgTxnSeries = averageSeries(txnSeries);
  const { start: gameStart, gates: gatesOpen } = gameTimes(currentGame);
  // Full-day activity (unfiltered) for the analysis window backdrop
  const fullAvgSeries = averageSeries(seriesBySection(isCountMode ? historicalPeopleCountData : historicalData));

  // "What stands out": findings over the analysis window
  const insights = buildConcessionDayInsights({ mainSeries, txnSeries, isCountMode, gameStart, isLive: false, countSectionBusyAt: COUNT_SECTION_HIGH, countAvgBusyAt: COUNT_AVG_HIGH, formatTransactionTime });

  const valueUnit = isCountMode ? 'people' : 'mins';
  const sectionStatus = (value) =>
    isCountMode ? getPeopleCountStatus(value, COUNT_SECTION_LOW, COUNT_SECTION_HIGH) : getQueueTimeStatus(value);
  const avgStatusFor = (value) =>
    isCountMode ? getPeopleCountStatus(value, COUNT_AVG_LOW, COUNT_AVG_HIGH) : getQueueTimeStatus(value);
  const sectionStatsFor = (id) => (isCountMode ? displayCountStats?.sectionStats?.[id] : displayStats?.sectionStats?.[id]);

  // Section averages over the range
  const sectionAverages = sections
    .map((id) => {
      const st = sectionStatsFor(id);
      const value = isCountMode ? st?.avgCount ?? 0 : st?.avgQueue ?? 0;
      return { name: id, value, status: sectionStatus(value), hasData: !!st };
    })
    .filter((s) => s.hasData);
  const byAverage = [...sectionAverages].sort((a, b) => b.value - a.value);
  const longestAvg = byAverage[0];
  const sectionsOver = sectionAverages.filter((s) => s.status === 'bad');

  // Headline numbers
  const avgValue = isCountMode ? displayCountStats?.dayAverageCount || 0 : displayStats?.dayAverageQueue || 0;
  const avgStatus = avgStatusFor(avgValue);
  const peakValue = isCountMode ? displayCountStats?.busiestTimeAvgCount || 0 : displayStats?.busiestTimeAvgQueue || 0;
  const peakAt = isCountMode ? displayCountStats?.busiestTime : displayStats?.busiestTime;
  const over = minutesOver(avgSeries, isCountMode ? COUNT_AVG_HIGH : QUEUE_TIME_GOOD_MAX, !isCountMode);

  // Overflow (queue mode only): queue extended past sensor coverage, recorded times are minimums
  const overflowFor = (id) => (isCountMode ? { minutes: 0, episodes: 0 } : overflowSummary(mainSeries[id] || []));
  const overflowRange = isCountMode ? { minutes: 0, episodes: 0 } : overflowAcrossSections(mainSeries);
  const sectionsWithOverflow = isCountMode ? [] : sections.filter((id) => overflowFor(id).minutes > 0);
  const avgPeak = peakOf(avgSeries);
  const peakOverflow = !isCountMode && !!avgPeak?.o && Math.round(avgPeak.y) === peakValue;
  // A section's longest queue gets "+" when that reading overflowed
  const maxQueueOverflow = (id, maxQueue) => {
    const peak = peakOf(mainSeries[id] || []);
    return !isCountMode && !!peak?.o && peak.y === maxQueue;
  };

  // Range shown in the status bar
  const rangeStart = appliedStartTime || earliestTime;
  const rangeEnd = appliedEndTime || latestTime;
  const isCustomRange = !!(appliedStartTime && appliedEndTime) &&
    (appliedStartTime.getTime?.() !== earliestTime?.getTime() || appliedEndTime.getTime?.() !== latestTime?.getTime());

  const tileDetails = (id) => {
    const st = sectionStatsFor(id);
    const overSection = minutesOver(mainSeries[id] || [], isCountMode ? COUNT_SECTION_HIGH : QUEUE_TIME_GOOD_MAX, !isCountMode);
    if (isCountMode) {
      return [
        { label: 'Highest count', value: `${st?.maxCount ?? 0} people`, at: st?.maxCountTime },
        { label: 'Time busy', value: formatDuration(overSection.minutes) },
      ];
    }
    const overflow = overflowFor(id);
    const longestOverflow = maxQueueOverflow(id, st?.maxQueue);
    // Pairs: queue | queue, overflow | overflow, transaction | transaction
    return [
      { label: 'Longest queue', value: `${formatQueueValue(st?.maxQueue ?? 0, longestOverflow)} min`, at: st?.maxQueueTime, alert: longestOverflow },
      { label: 'Over target', value: formatDuration(overSection.minutes) },
      { label: 'Overflow duration', value: overflow.minutes > 0 ? formatDuration(overflow.minutes) : 'None', alert: overflow.minutes > 0 },
      { label: 'Times in overflow', value: `${overflow.episodes}`, alert: overflow.episodes > 0 },
      { label: 'Avg transaction', value: formatTransactionTime(st?.avgTransaction || 0) },
      { label: 'Peak transaction', value: formatTransactionTime(st?.maxTransaction || 0), at: st?.maxTransactionTime },
    ];
  };

  const trendOptions = isCountMode
    ? []
    : [
        { value: 'queue', label: 'Queue times' },
        { value: 'transaction', label: 'Transactions' },
      ];

  const chartQueueData = filteredHistoricalData.length > 0 ? filteredHistoricalData : historicalData;
  const chartTransactionData = filteredHistoricalTransactionData.length > 0 ? filteredHistoricalTransactionData : historicalTransactionData;
  const chartCountData = filteredHistoricalPeopleCountData.length > 0 ? filteredHistoricalPeopleCountData : historicalPeopleCountData;

  return (
    <div className="cx-live">
      {/* HISTORICAL VIEW - Past Game Day */}

      {/* Status bar: final game, schedule, selected range */}
      <div className="cx-live-status">
        <div className="cx-live-status-left">
          <span className="cx-phase cx-phase-final">Final</span>
          {currentGame?.date && (
            <span className="cx-live-status-detail">{moment(currentGame.date).format('ddd, MMM D, YYYY')}</span>
          )}
          {gatesOpen && <span className="cx-live-status-meta">Gates {formatClock(gatesOpen)}</span>}
          {gameStart && <span className="cx-live-status-meta">Game start {formatClock(gameStart)}</span>}
        </div>
        <div className="cx-live-status-right">
          <span className="cx-updated">
            Showing {formatClock(rangeStart)} – {formatClock(rangeEnd)}
          </span>
          <span className="cx-live-status-meta">{isCustomRange ? 'Custom range' : 'Full day'}</span>
        </div>
      </div>

      {/* Time Range filter */}
      {earliestTime && latestTime && (
        <div className="mb-4">
          <Panel>
            <PanelHeader
              title="Analysis window"
              subtitle="Pick a preset or drag the handles. Every number and chart below follows this range."
            />
            <AnalysisWindow
              earliestTime={earliestTime}
              latestTime={latestTime}
              onRangeChange={handleTimeRangeChange}
              activity={fullAvgSeries}
              gameStart={gameStart}
              gatesOpen={gatesOpen}
              target={isCountMode ? undefined : QUEUE_TIME_GOOD_MAX}
            />
          </Panel>
        </div>
      )}

      {/* Overflow notice for the selected range */}
      {overflowRange.minutes > 0 && (
        <OverflowAlert
          title={`Overflow recorded for ${formatDuration(overflowRange.minutes)} across ${sectionsWithOverflow.map((id) => `Section ${id}`).join(', ')}`}
        />
      )}

      {/* Headline KPIs */}
      <div className={`cx-kpi-grid${hasSlot(extension, 'gameDay.kpis') ? ' cx-kpi-grid-auto' : ''}`}>
        <KpiCard
          label={isCountMode ? 'Average count' : 'Average queue'}
          info={isCountMode ? 'Average count of all sections. Averages use every reading, so they can sit a little below the charts, which show the highest reading in each interval.' : `Average queue of all sections. Under ${QUEUE_TIME_GOOD_MAX} min is on target; ${QUEUE_TIME_GOOD_MAX} min or more is over. Averages use every reading, so they can sit a little below the charts, which show the highest reading in each interval.`}
          accent={STATUS_COLORS[avgStatus]}
          value={<>{avgValue}{unit(valueUnit)}</>}
        >
          <Sparkline
            points={avgSeries}
            color="var(--q-text-subtle)"
            valueColor={(v) => STATUS_COLORS[avgStatusFor(v)]}
            target={isCountMode ? undefined : QUEUE_TIME_GOOD_MAX}
            minMax={isCountMode ? COUNT_AVG_HIGH : QUEUE_TIME_GOOD_MAX * 1.5}
            formatValue={unitFormatter(isCountMode ? 'people' : 'min')}
          />
          <div className="cx-kpi-foot">Across the selected range</div>
        </KpiCard>

        <KpiCard
          label={isCountMode ? 'Peak average count' : 'Peak average queue'}
          info="Highest average across all sections at any single minute."
          accent={peakOverflow ? STATUS_COLORS.bad : STATUS_COLORS[avgStatusFor(peakValue)]}
          value={<>{peakValue}{peakOverflow && <span className="cx-plus">+</span>}{unit(valueUnit)}</>}
        >
          <div className="cx-kpi-sub">{peakAt ? `at ${peakAt}` : 'No peak recorded'}</div>
          {showDeltaStats && (
            <div className="cx-kpi-foot">
              Peak delta {displayStats?.historicalMaxDelta || 0} min{displayStats?.historicalMaxDeltaTime ? ` at ${displayStats.historicalMaxDeltaTime}` : ''}
            </div>
          )}
        </KpiCard>

        <KpiCard
          label={isCountMode ? 'Time busy' : 'Time over target'}
          info={isCountMode
            ? `Time the average count was above ${COUNT_AVG_HIGH} people. Counts actual time from every reading; charts show the highest reading in each interval, so they can look slightly higher.`
            : `Time the average queue was above ${QUEUE_TIME_GOOD_MAX} min. Counts actual time from every reading; charts show the highest reading in each interval, so they can look slightly higher.`}
          accent={over.minutes > 0 ? STATUS_COLORS.bad : STATUS_COLORS.good}
          value={formatDuration(over.minutes)}
        >
          <div className="cx-kpi-sub">{Math.round(over.share * 100)}% of the range</div>
          <div className="cx-section-dots">
            {sectionAverages.map((s) => (
              <span key={s.name} className="cx-section-dot" style={{ background: STATUS_COLORS[s.status] }} title={`Section ${s.name}: ${s.value} ${valueUnit} average`}>
                {s.name}
              </span>
            ))}
          </div>
          <div className="cx-kpi-foot">
            {sectionsOver.length === 0
              ? 'Every section averaged on target'
              : `Averaged over target: ${sectionsOver.map((s) => s.name).join(', ')}`}
            {overflowRange.minutes > 0 && ` · Includes ${formatDuration(overflowRange.minutes)} of overflow`}
          </div>
        </KpiCard>

        {isCountMode ? (
          <KpiCard
            label="Busiest section"
            info="Section with the highest average count."
            accent={longestAvg ? STATUS_COLORS[longestAvg.status] : undefined}
            value={<>{longestAvg?.value ?? 0}{unit('people')}</>}
          >
            <div className="cx-kpi-sub">{longestAvg ? `Section ${longestAvg.name}` : 'No sections'}</div>
            <div className="cx-kpi-foot">Average count</div>
          </KpiCard>
        ) : (
          <KpiCard
            label="Average transaction"
            info="Average register transaction time of all sections."
            value={formatTransactionTime(displayStats?.dayAverageTransaction || 0)}
          >
            <Sparkline points={avgTxnSeries} color="var(--q-brand)" height={36} formatValue={(v) => formatTransactionTime(v)} />
            <div className="cx-kpi-foot">
              Peak {formatTransactionTime(displayStats?.peakTransactionAvg || 0)}
              {displayStats?.peakTransactionTime ? ` at ${displayStats.peakTransactionTime}` : ''}
            </div>
          </KpiCard>
        )}
        {/* Venue-specific cards (extensions/registry.js) */}
        <Slot
          name="gameDay.kpis"
          extension={extension}
          authCtx={authCtx}
          currentLocation={currentLocation}
          currentGame={currentGame}
          selectedDate={selectedDate}
          isLive={false}
          refreshKey={historicalData}
        />
      </div>

      {insights.length > 0 && (
        <div className="mb-4">
          <Panel>
            <PanelHeader
              title="What stands out"
              subtitle={`Findings ${isCustomRange ? `for ${formatClock(rangeStart)} – ${formatClock(rangeEnd)}` : 'from this game'}, most important first. They follow the analysis window.`}
            />
            <OverviewInsights insights={insights} />
          </Panel>
        </div>
      )}

      {/* Section board + side panels: adaptive so nothing stretches into dead space */}
      <AdaptiveBoardLayout
        pinFirst
        board={
          <Panel>
            <PanelHeader
              title="Sections"
              info="Section statistics over the selected range. Transaction times are an average of all registers in that section."
              subtitle={isCountMode ? 'Average count by section across the range.' : 'Average queue by section across the range. Dashed line is the target.'}
              actions={
                <div className="cx-panel-actions-row">
                  <ChartSegmented
                    options={[{ value: 'cards', label: 'Cards' }, { value: 'table', label: 'Table' }]}
                    value={sectionView}
                    onChange={setSectionView}
                  />
                  <SortMenu
                    isOpen={isSectionAnalysisDropdownOpen}
                    onToggle={toggleSectionAnalysisDropdown}
                    options={sectionAnalysisSortOptions}
                    current={currentSectionAnalysisSort}
                    onChange={handleSectionAnalysisSortChange}
                    minWidth={320}
                  />
                </div>
              }
            />

            {sectionView === 'cards' ? (
              <div className="cx-tile-grid">
                {sortedSectionAnalysisSections.map((sectionId) => {
                  const st = sectionStatsFor(sectionId);
                  if (!st) return null;
                  const value = isCountMode ? st.avgCount : st.avgQueue;
                  const status = sectionStatus(value);
                  return (
                    <SectionTile
                      key={sectionId}
                      name={`Section ${sectionId}`}
                      value={value}
                      unit={valueUnit}
                      valueCaption={isCountMode ? 'average count' : 'average queue'}
                      status={status}
                      statusFor={(v) => sectionStatus(v)}
                      statusLabel={(isCountMode ? STATUS_LABELS.count : STATUS_LABELS.queue)[status]}
                      hideTrend
                      overflowLabel={overflowFor(sectionId).minutes > 0 ? `Overflow ${formatDuration(overflowFor(sectionId).minutes)}` : null}
                      spark={mainSeries[sectionId] || []}
                      target={isCountMode ? undefined : QUEUE_TIME_GOOD_MAX}
                      sparkMin={isCountMode ? COUNT_SECTION_HIGH : QUEUE_TIME_GOOD_MAX * 1.5}
                      details={tileDetails(sectionId)}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="cx-table-wrap">
                <table className="cx-table">
                  <thead>
                    <tr>
                      <th>Section</th>
                      <th>{isCountMode ? 'Highest Count' : 'Longest Queue'}</th>
                      <th>{isCountMode ? 'Average Count' : 'Average Queue'}</th>
                      {!isCountMode && <th>Over Target</th>}
                      {!isCountMode && <th>Overflow Duration</th>}
                      {!isCountMode && <th>Times in Overflow</th>}
                      {!isCountMode && <th>Peak Transaction Time</th>}
                      {!isCountMode && <th>Average Transaction Time</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedSectionAnalysisSections.map((sectionId) => {
                      // Get the appropriate section stats based on mode (using filtered stats)
                      const sectionStat = sectionStatsFor(sectionId);
                      if (!sectionStat) return null;

                      return (
                        <tr key={sectionId}>
                          <td className="cx-table-section">Section {sectionId}</td>
                          <td>
                            <div className="cx-table-value">
                              {isCountMode
                                ? <>{sectionStat.maxCount}{unit('people')}</>
                                : <span style={{ color: STATUS_COLORS[getQueueTimeStatus(sectionStat.maxQueue, maxQueueOverflow(sectionId, sectionStat.maxQueue))] }}>{formatQueueValue(sectionStat.maxQueue, maxQueueOverflow(sectionId, sectionStat.maxQueue))}{unit('mins')}</span>
                              }
                            </div>
                            <div className="cx-table-time">
                              {isCountMode ? sectionStat.maxCountTime : sectionStat.maxQueueTime}
                            </div>
                          </td>
                          <td>
                            <div className="cx-table-value">
                              {isCountMode
                                ? <>{sectionStat.avgCount}{unit('people')}</>
                                : <span style={{ color: STATUS_COLORS[getQueueTimeStatus(sectionStat.avgQueue)] }}>{sectionStat.avgQueue}{unit('mins')}</span>
                              }
                            </div>
                          </td>
                          {!isCountMode && (
                            <td>
                              <div className="cx-table-value">
                                {formatDuration(minutesOver(mainSeries[sectionId] || [], QUEUE_TIME_GOOD_MAX, true).minutes)}
                              </div>
                            </td>
                          )}
                          {!isCountMode && (
                            <td>
                              {overflowFor(sectionId).minutes > 0 ? (
                                <div className="cx-table-value" style={{ color: STATUS_COLORS.bad }}>{formatDuration(overflowFor(sectionId).minutes)}</div>
                              ) : (
                                <div className="cx-table-value cx-table-muted">None</div>
                              )}
                            </td>
                          )}
                          {!isCountMode && (
                            <td>
                              <div
                                className={`cx-table-value${overflowFor(sectionId).episodes > 0 ? '' : ' cx-table-muted'}`}
                                style={overflowFor(sectionId).episodes > 0 ? { color: STATUS_COLORS.bad } : undefined}
                              >
                                {overflowFor(sectionId).episodes}
                              </div>
                            </td>
                          )}
                          {!isCountMode && (
                            <td>
                              <div className="cx-table-value">{formatTransactionTime(sectionStat.maxTransaction || 0)}</div>
                              <div className="cx-table-time">{sectionStat.maxTransactionTime}</div>
                            </td>
                          )}
                          {!isCountMode && (
                            <td>
                              <div className="cx-table-value">{formatTransactionTime(sectionStat.avgTransaction || 0)}</div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        }
        panels={[
          { key: 'summary', node: (
            <Panel>
              <PanelHeader title="Game summary" info={"Statistics of all sections over the selected range."+" Averages use every reading, so they can sit a little below the charts, which show the highest reading in each interval."} />
              <DaySummary
                queue={isCountMode
                  ? {
                      title: 'People count',
                      avg: displayCountStats?.dayAverageCount || 0,
                      peak: displayCountStats?.busiestTimeAvgCount || 0,
                      peakAt: displayCountStats?.busiestTime,
                      avgStatus: avgStatusFor(displayCountStats?.dayAverageCount || 0),
                      statusFor: avgStatusFor,
                      peakStatus: avgStatusFor(displayCountStats?.busiestTimeAvgCount || 0),
                      unit: 'people',
                    }
                  : {
                      title: 'Queue',
                      avg: displayStats?.dayAverageQueue || 0,
                      peak: displayStats?.busiestTimeAvgQueue || 0,
                      peakAt: displayStats?.busiestTime,
                      peakOverflow,
                      avgStatus: getQueueTimeStatus(displayStats?.dayAverageQueue || 0),
                      statusFor: (v) => getQueueTimeStatus(v),
                      peakStatus: getQueueTimeStatus(displayStats?.busiestTimeAvgQueue || 0, peakOverflow),
                      target: QUEUE_TIME_GOOD_MAX,
                      unit: 'min',
                    }}
                time={{
                  total: over.total,
                  over: over.minutes,
                  overflow: overflowRange.minutes,
                  overflowEpisodes: overflowRange.episodes,
                  overflowSections: sectionsWithOverflow,
                  overLabel: isCountMode ? 'Time busy' : 'Time over target',
                }}
                delta={showDeltaStats ? {
                  avg: displayStats?.dayAverageDelta || 0,
                  peak: displayStats?.historicalMaxDelta || 0,
                  peakAt: displayStats?.historicalMaxDeltaTime,
                } : null}
                transaction={isCountMode ? null : {
                  avg: displayStats?.dayAverageTransaction || 0,
                  peak: displayStats?.peakTransactionAvg || 0,
                  peakAt: displayStats?.peakTransactionTime,
                  format: formatTransactionTime,
                }}
                series={{ main: avgSeries, txn: avgTxnSeries, delta: deltaSeries(mainSeries) }}
                overThreshold={isCountMode ? COUNT_AVG_HIGH : QUEUE_TIME_GOOD_MAX}
                overAtOrAbove={!isCountMode}
              />
            </Panel>
          ) },
        ]}
      />

      {/* Trends - Using filtered data */}
      {(historicalData.length > 0 || historicalPeopleCountData.length > 0) && (
        <div className={`cx-trends${trendsOpen ? ' open' : ''}`}>
          <div className="cx-trends-head">
            <button
              type="button"
              className="cx-trends-toggle"
              onClick={() => setTrendsOpen((o) => !o)}
              aria-expanded={trendsOpen}
            >
              <Icon name={trendsOpen ? 'chevron-down' : 'chevron-right'} className="cx-trends-chevron" />
              <span>
                <span className="cx-panel-title">Section trends</span>
                <span className="cx-panel-subtitle">Each section over the selected range. Zoom, pan and compare sections side by side.</span>
              </span>
            </button>
            {trendsOpen && trendOptions.length > 1 && (
              <ChartSegmented options={trendOptions} value={trendTab} onChange={setTrendTab} />
            )}
          </div>

          {trendsOpen && (isCountMode ? (
            chartCountData.length > 0 && (
              <ConcessionCountChart
                historicalPeopleCountData={chartCountData}
                sections={sections}
                currentGame={currentGame}
              />
            )
          ) : (
            <>
              {trendTab === 'queue' && chartQueueData.length > 0 && (
                <ConcessionChart
                  name={currentLocation.name}
                  sections={sections}
                  historicalData={chartQueueData}
                  currentGame={currentGame}
                />
              )}
              {trendTab === 'transaction' && chartTransactionData.length > 0 && (
                <ConcessionTransactionChart
                  historicalTransactionData={chartTransactionData}
                  sections={sections}
                  formatTransactionTime={formatTransactionTime}
                  currentGame={currentGame}
                />
              )}
            </>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConcessionLocationHistoricalView;
